import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Grid,
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import useStyles from './styles';
import DashboardSummaryCard from './components/DashboardSummaryCard';
import FinancialChart from './components/chart';
import { getDashboardSummary, DashboardSummaryResponse } from '../../requests/api/dashboard';
import { useYear } from '../../hooks/useYear';

const referenceColors = ['#E2E2E2', '#706E6F', '#2C82B3', '#5BC2D5', '#F2AC32'];

const AcademicDashboard: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const { selectedYear } = useYear();
  const [dashboardData, setDashboardData] = useState<DashboardSummaryResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        const data = await getDashboardSummary(selectedYear);
        setDashboardData(data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [selectedYear]);

  const formatPercentage = (percentage: number): string => {
    const sign = percentage >= 0 ? '+' : '';
    return `${sign}${percentage.toFixed(2)}%`;
  };

  const formatNumber = (num: number): string => num.toLocaleString('es-CL');

  if (loading) {
    return (
      <Box
        className={classes.root}
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!dashboardData) {
    return (
      <Box className={classes.root}>
        <Typography>No se pudieron cargar los datos del dashboard</Typography>
      </Box>
    );
  }

  const { metrics, charts } = dashboardData;

  const studentsBySubjectItems = Object.entries(metrics.studentsBySubject)
    .map(([subject, count], index) => ({
      label: subject,
      value: formatNumber(count),
      color: referenceColors[index % referenceColors.length],
    }));

  const studentsByLevelItems = Object.entries(metrics.studentsByLevel)
    .map(([level, count], index) => ({
      label: level,
      value: formatNumber(count),
      color: referenceColors[index % referenceColors.length],
    }));

  const currentYearStudents = charts.studentsByMonth[selectedYear]?.map((item) => item.value)
    || new Array(12).fill(0);
  const lastYearStudents = charts.studentsByMonth[selectedYear - 1]?.map((item) => item.value)
    || new Array(12).fill(0);

  const currentYearPrograms = charts.programsByMonth[selectedYear]?.map((item) => item.value)
    || new Array(12).fill(0);
  const lastYearPrograms = charts.programsByMonth[selectedYear - 1]?.map((item) => item.value)
    || new Array(12).fill(0);

  return (
    <Box className={classes.dashboardSectionRoot}>

      <Box className={classes.mainCardsRow}>
        <Box display="flex" flex={1}>
          <Box className={`${classes.totalCard} ${classes.cardContent}`}>
            <Box className={classes.titleContainer}>
              <Typography className={classes.totalLabel}>
                Alumnos inscritos
              </Typography>
              <ArrowForwardIosIcon
                onClick={() => history.push('/dashboard/academico/inscripciones/alumnos')}
                style={{ cursor: 'pointer' }}
              />
            </Box>
            <Box className={classes.valueRow}>
              <Typography className={classes.totalAmount}>
                {formatNumber(metrics.totalEnrolledStudents.value)}
              </Typography>
              {selectedYear > 2026
                && metrics.totalEnrolledStudents.percentageChange !== 0 && (
                  <Box
                    className={classes.percentage}
                    sx={{
                      color: metrics.totalEnrolledStudents.percentageChange >= 0
                        ? 'success.main' : 'error.main',
                      '& svg': {
                        color: metrics.totalEnrolledStudents.percentageChange >= 0
                          ? 'success.main' : 'error.main',
                      },
                    }}
                  >
                    {metrics.totalEnrolledStudents.percentageChange >= 0 ? (
                      <TrendingUpIcon />
                    ) : (
                      <TrendingDownIcon />
                    )}
                    <Typography className={classes.percentageText}>
                      {formatPercentage(metrics.totalEnrolledStudents.percentageChange)}
                    </Typography>
                  </Box>
              )}
            </Box>
            <Typography className={classes.totalDescription}>
              Total acumulado en {selectedYear}
            </Typography>
          </Box>

          <Box className={`${classes.totalCard} ${classes.cardContent}`}>
            <Box className={classes.titleContainer}>
              <Typography className={classes.totalLabel}>
                Programas inscritos
              </Typography>
              <ArrowForwardIosIcon
                onClick={() => history.push('/dashboard/academico/inscripciones/programas')}
                style={{ cursor: 'pointer' }}
              />
            </Box>
            <Box className={classes.valueRow}>
              <Typography className={classes.totalAmount}>
                {formatNumber(metrics.totalEnrolledPrograms.value)}
              </Typography>
              {selectedYear > 2026
                && metrics.totalEnrolledPrograms.percentageChange !== 0 && (
                  <Box
                    className={classes.percentage}
                    sx={{
                      color: metrics.totalEnrolledPrograms.percentageChange >= 0
                        ? 'success.main' : 'error.main',
                      '& svg': {
                        color: metrics.totalEnrolledPrograms.percentageChange >= 0
                          ? 'success.main' : 'error.main',
                      },
                    }}
                  >
                    {metrics.totalEnrolledPrograms.percentageChange >= 0 ? (
                      <TrendingUpIcon />
                    ) : (
                      <TrendingDownIcon />
                    )}
                    <Typography className={classes.percentageText}>
                      {formatPercentage(metrics.totalEnrolledPrograms.percentageChange)}
                    </Typography>
                  </Box>
              )}
            </Box>
            <Typography className={classes.totalDescription}>
              Total acumulado en {selectedYear}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={6}>
          <DashboardSummaryCard
            title="Alumnos por asignatura"
            items={studentsBySubjectItems}
            onNavigate={() => history.push('/dashboard/academico/alumnos-por-asignatura')}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <DashboardSummaryCard
            title="Alumnos por nivel"
            items={studentsByLevelItems}
            onNavigate={() => history.push('/dashboard/academico/alumnos-por-nivel')}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Box className={classes.academicChartCard}>
            <Box className={classes.chartHeader}>
              <Typography variant="subtitle2" className={classes.totalLabel}>
                Total de alumnos inscritos por año
              </Typography>
            </Box>
            <FinancialChart
              monthlyPaid={currentYearStudents}
              monthlyPaidLastYear={lastYearStudents}
              currentYearLabel={`${selectedYear}`}
              lastYearLabel={`${selectedYear - 1}`}
              valuePrefix=""
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box className={classes.academicChartCard}>
            <Box className={classes.chartHeader}>
              <Typography variant="subtitle2" className={classes.totalLabel}>
                Total de programas vendidos por año
              </Typography>
            </Box>
            <FinancialChart
              monthlyPaid={currentYearPrograms}
              monthlyPaidLastYear={lastYearPrograms}
              currentYearLabel={`${selectedYear}`}
              lastYearLabel={`${selectedYear - 1}`}
              valuePrefix=""
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AcademicDashboard;
